import { useState } from "react";
import FadeIn from "../components/FadeIn";
import { ME } from "../constants/portfolioData";

/**
 * RecommendationsSection
 * Displays LinkedIn-style recommendations in a paginated card layout.
 * Shows 2 cards at a time with prev/next navigation.
 */
const RecommendationsSection = () => {
  const { recommendations } = ME;
  const [page, setPage] = useState(0);

  const perPage = 2;
  const totalPages = Math.ceil(recommendations.length / perPage);
  const visible = recommendations.slice(page * perPage, page * perPage + perPage);

  return (
    <>
      <style>{`
        /* ── SECTION ── */
        #recommendations { background: var(--bg); }

        /* ── GRID ── */
        .rec-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-bottom: 2.5rem;
        }

        /* ── CARD ── */
        .rec-card {
          background: var(--bg2);
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          transition: box-shadow 0.25s, border-color 0.25s;
          position: relative;
        }
        .rec-card:hover {
          border-color: var(--border2);
          box-shadow: 0 8px 32px #1d4ed80a;
        }

        /* opening quote mark */
        .rec-quote-mark {
          position: absolute;
          top: 1.25rem;
          right: 1.5rem;
          font-family: 'IBM Plex Serif', Georgia, serif;
          font-size: 4rem;
          line-height: 1;
          color: var(--accent);
          opacity: 0.12;
          pointer-events: none;
          user-select: none;
        }

        /* ── STARS ── */
        .rec-stars {
          display: flex;
          gap: 0.2rem;
        }
        .rec-star {
          color: #f59e0b;
          font-size: 0.85rem;
        }

        /* ── BODY TEXT ── */
        .rec-text {
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 0.925rem;
          line-height: 1.85;
          color: var(--muted2);
          flex: 1;
          font-style: italic;
        }

        /* ── DIVIDER ── */
        .rec-divider {
          width: 100%;
          height: 1px;
          background: var(--border);
        }

        /* ── AUTHOR ROW ── */
        .rec-author {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }
        .rec-author-left {
          display: flex;
          align-items: center;
          gap: 0.85rem;
        }

        /* avatar circle */
        .rec-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: var(--accent);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 0.8rem;
          font-weight: 700;
          flex-shrink: 0;
          letter-spacing: 0.02em;
        }

        .rec-name {
          font-family: 'IBM Plex Sans', sans-serif;
          font-weight: 700;
          font-size: 0.9rem;
          color: var(--text);
          margin-bottom: 0.1rem;
        }
        .rec-role-line {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.72rem;
          color: var(--muted);
        }
        .rec-company {
          color: var(--accent2);
          font-weight: 500;
        }
        .rec-relation {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.68rem;
          color: var(--muted);
          margin-top: 0.25rem;
        }

        /* LinkedIn badge */
        .rec-linkedin {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.3rem 0.65rem;
          background: #0a66c210;
          border: 1px solid #0a66c230;
          border-radius: 6px;
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.68rem;
          color: #0a66c2;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .rec-linkedin:hover {
          background: #0a66c220;
          border-color: #0a66c2;
        }
        .rec-linkedin-dot {
          width: 6px;
          height: 6px;
          background: #0a66c2;
          border-radius: 1px;
        }

        /* ── PAGINATION ── */
        .rec-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
        }
        .rec-pg-btn {
          width: 38px;
          height: 38px;
          border: 1px solid var(--border2);
          border-radius: 8px;
          background: none;
          color: var(--muted2);
          font-size: 1rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .rec-pg-btn:hover:not(:disabled) {
          border-color: var(--accent2);
          color: var(--accent2);
          background: var(--accent-dim);
        }
        .rec-pg-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .rec-dots {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }
        .rec-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--border2);
          transition: all 0.2s;
          cursor: pointer;
          border: none;
          padding: 0;
        }
        .rec-dot.active {
          background: var(--accent);
          width: 22px;
          border-radius: 4px;
        }

        /* ── SUMMARY BAR ── */
        .rec-summary {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2rem;
          margin-bottom: 3rem;
          padding: 1.25rem 2rem;
          background: var(--bg2);
          border: 1px solid var(--border);
          border-radius: 12px;
        }
        .rec-summary-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.2rem;
        }
        .rec-summary-n {
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text);
        }
        .rec-summary-l {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.68rem;
          color: var(--muted);
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }
        .rec-summary-sep {
          width: 1px;
          height: 36px;
          background: var(--border2);
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 720px) {
          .rec-grid { grid-template-columns: 1fr; }
          .rec-summary { flex-wrap: wrap; gap: 1.25rem; }
          .rec-summary-sep { display: none; }
        }
      `}</style>

      <section id="recommendations">
        <div className="wrap">
          <FadeIn><div className="sec-eyebrow">Social Proof</div></FadeIn>
          <FadeIn delay={0.05}>
            <h2 className="sec-title">What colleagues <em>say about me</em></h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="sec-sub">
              Recommendations from engineers, managers, and leaders I've worked with directly.
            </p>
          </FadeIn>

          {/* Summary bar */}
          <FadeIn delay={0.15}>
            <div className="rec-summary">
              <div className="rec-summary-item">
                <div className="rec-summary-n">{recommendations.length}</div>
                <div className="rec-summary-l">Recommendations</div>
              </div>
              <div className="rec-summary-sep" />
              <div className="rec-summary-item">
                <div className="rec-summary-n" style={{ display: "flex", gap: "0.1rem" }}>
                  {"★★★★★".split("").map((s, i) => (
                    <span key={i} style={{ color: "#f59e0b", fontSize: "1.1rem" }}>{s}</span>
                  ))}
                </div>
                <div className="rec-summary-l">Average Rating</div>
              </div>
              <div className="rec-summary-sep" />
              <div className="rec-summary-item">
                <div className="rec-summary-n">
                  {[...new Set(recommendations.map(r => r.company))].length}
                </div>
                <div className="rec-summary-l">Companies</div>
              </div>
              <div className="rec-summary-sep" />
              <div className="rec-summary-item">
                <div className="rec-summary-n">
                  {[...new Set(recommendations.map(r => {
                    if (r.relation.toLowerCase().includes("manager")) return "Manager";
                    if (r.relation.toLowerCase().includes("peer") || r.relation.toLowerCase().includes("lead")) return "Peer";
                    return "Client";
                  }))].length}+
                </div>
                <div className="rec-summary-l">Relation Types</div>
              </div>
            </div>
          </FadeIn>

          {/* Cards */}
          <div className="rec-grid">
            {visible.map((rec, i) => (
              <FadeIn key={rec.name} delay={i * 0.1}>
                <div className="rec-card">
                  <div className="rec-quote-mark">"</div>

                  {/* Stars */}
                  <div className="rec-stars">
                    {[1,2,3,4,5].map(s => (
                      <span key={s} className="rec-star">★</span>
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="rec-text">"{rec.text}"</p>

                  <div className="rec-divider" />

                  {/* Author */}
                  <div className="rec-author">
                    <div className="rec-author-left">
                      <div className="rec-avatar">{rec.avatar}</div>
                      <div>
                        <div className="rec-name">{rec.name}</div>
                        <div className="rec-role-line">
                          {rec.role} · <span className="rec-company">{rec.company}</span>
                        </div>
                        <div className="rec-relation">{rec.relation}</div>
                      </div>
                    </div>
                    <a
                      href={rec.linkedin}
                      className="rec-linkedin"
                      target="_blank"
                      rel="noreferrer"
                      title="View on LinkedIn"
                    >
                      <div className="rec-linkedin-dot" />
                      LinkedIn
                    </a>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <FadeIn delay={0.2}>
              <div className="rec-pagination">
                <button
                  className="rec-pg-btn"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 0}
                  aria-label="Previous"
                >
                  ←
                </button>

                <div className="rec-dots">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      className={`rec-dot ${page === i ? "active" : ""}`}
                      onClick={() => setPage(i)}
                      aria-label={`Page ${i + 1}`}
                    />
                  ))}
                </div>

                <button
                  className="rec-pg-btn"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === totalPages - 1}
                  aria-label="Next"
                >
                  →
                </button>
              </div>
            </FadeIn>
          )}
        </div>
      </section>
    </>
  );
};

export default RecommendationsSection;